import { PurchaseOrderItemStatus } from '@domain/purchasing';
import { ApiClient } from '../../ApiClient';
import { CreatePurchaseOrderItemDTO, CreatePurchaseOrderDTO } from '../dto/CreatePurchaseOrderDTO';
import { PurchaseOrderDTO, PurchaseOrderItemDTO } from '../dto/PurchaseOrderDTO';
import { ShipmentDTO } from '../dto/ShipmentDTO';
import { UpdatePurchaseOrderDTO, UpdatePurchaseOrderItemStatusDTO, UpdatePurchaseOrderItemSortDTO } from '../dto/UpdatePurchaseDTO';
import { PurchaseOrderRecommendationDTO } from '../dto/PurchaseOrderRecommendationDTO';

export class PurchasingDataSource {
  constructor(private api: ApiClient) { }

  async getPurchaseOrders(filters?: { draft?: boolean }): Promise<PurchaseOrderDTO[]> {
    const response = await this.api.get<PurchaseOrderDTO[]>('/purchase-orders', {
      params: filters
    });
    return response.data;
  }

  async getPurchaseOrder(id: string): Promise<PurchaseOrderDTO> {
    const response = await this.api.get<PurchaseOrderDTO>(`/purchase-orders/${id}`);
    return response.data;
  }

  async createPurchaseOrder(order: CreatePurchaseOrderDTO): Promise<PurchaseOrderDTO> {
    const response = await this.api.post<PurchaseOrderDTO>('/purchase-orders', order);
    return response.data;
  }

  async updatePurchaseOrder(id: string, updates: Partial<UpdatePurchaseOrderDTO>): Promise<PurchaseOrderDTO> {
    const response = await this.api.put<PurchaseOrderDTO>(`/purchase-orders/${id}`, updates);
    return response.data;
  }

  async publishPurchaseOrder(id: string): Promise<PurchaseOrderDTO> {
    const response = await this.api.post<PurchaseOrderDTO>(`/purchase-orders/${id}/publish`);
    return response.data;
  }

  async deletePurchaseOrder(id: string): Promise<void> {
    await this.api.delete(`/purchase-orders/${id}`);
  }

  async deletePurchaseOrderItem(purchaseOrderId: string, itemId: string): Promise<PurchaseOrderDTO> {
    const response = await this.api.delete<PurchaseOrderDTO>(`/purchase-orders/${purchaseOrderId}/items/${itemId}`);
    return response.data;
  }

  async updatePurchaseOrderItem(itemId: string, updates: { quantity?: number; productionStartDate?: Date; productionReadyDate?: Date, shipmentId?: string, eta?: Date }): Promise<PurchaseOrderItemDTO> {
    const response = await this.api.put<PurchaseOrderItemDTO>(`/purchase-orders/items/${itemId}`, updates);
    return response.data;
  }


  async getPurchaseOrderItemById(id: string): Promise<PurchaseOrderItemDTO> {
    const response = await this.api.get<PurchaseOrderItemDTO>(`/purchase-orders/items/${id}`);
    return response.data;
  }

  async getPurchaseOrderItems(filters?: {
    productId?: string;
    purchaseOrderId?: string;
    shipmentId?: string;
    supplierId?: string;
    availableDaysAgo?: number;
  }): Promise<PurchaseOrderItemDTO[]> {
    const response = await this.api.get<PurchaseOrderItemDTO[]>(`/purchase-orders/items`, {
      params: filters
    });
    return response.data;
  }

  async calculateAmounts(purchaseOrderId: string): Promise<PurchaseOrderRecommendationDTO[]> {
    const response = await this.api.get<PurchaseOrderRecommendationDTO[]>(
      `/purchase-orders/${purchaseOrderId}/calculate-amounts`,
    );
    return response.data;
  }

  async getActiveShipments(): Promise<ShipmentDTO[]> {
    const response = await this.api.get<ShipmentDTO[]>('/purchase-orders/shipments/active');
    return response.data;
  }

  async updatePurchaseOrderItemStatus(itemId: string, status: PurchaseOrderItemStatus): Promise<PurchaseOrderItemDTO> {
    const updateData: UpdatePurchaseOrderItemStatusDTO = { status };
    const response = await this.api.put<PurchaseOrderItemDTO>(`/purchase-orders/items/${itemId}`, updateData);
    return response.data;
  }

  async updatePurchaseOrderItemSort(itemId: string, sort: number): Promise<PurchaseOrderItemDTO> {
    const updateData: UpdatePurchaseOrderItemSortDTO = { sort };
    const response = await this.api.put<PurchaseOrderItemDTO>(`/purchase-orders/items/${itemId}`, updateData);
    return response.data;
  }

  async createPurchaseOrderItem(purchaseOrderId: string, item: CreatePurchaseOrderItemDTO): Promise<PurchaseOrderItemDTO> {
    const response = await this.api.post<PurchaseOrderItemDTO>(`/purchase-orders/${purchaseOrderId}/items`, item);
    return response.data;
  }

  async getPurchaseOrderPdf(purchaseOrderId: string): Promise<Blob> {
    const response = await this.api.get<Blob>(`/purchase-orders/${purchaseOrderId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }
} 